import { ParsedUrlQuery } from 'querystring';

import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { getSelectorsByUserAgent } from 'react-device-detect';

import { Layout } from '@/components/Layout';
import { PlantMapLocation } from '@/components/PlantMapLocation';
import { QrScannerComponent } from '@/components/QrScannerComponent';
import { Button } from '@/components/shared/Button';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { Image } from '@/components/shared/Image';
import { ImageSlider } from '@/components/shared/ImageSlider';
import { Link } from '@/components/shared/Link';
import { Modal } from '@/components/shared/Modal';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { usePlantSeo } from '@/hooks/usePlantSeo';
import { useToggle } from '@/hooks/useToggle';
import { COMMON_LOCALE_PART } from '@/locale';
import { Colors, IPageWithMenuProps, IPlantDetails, IPlantImage, Locale, SiteMenuItem } from '@/types';
import { useLocale } from '@/hooks/useLocale';
interface IPlantDetailsPageProps extends IPageWithMenuProps {
  plantDetails: IPlantDetails;
}

const NATURAL_HABITAT_CHARACTER_LIMIT = 64;

const PlantTypePage = ({ siteMenu, plantDetails }: IPlantDetailsPageProps) => {
  const { t } = useTranslation(COMMON_LOCALE_PART);

  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);

  const [isQrScanning, toggleQrScanning] = useToggle();
  const [expandMap, toggleExpandMap] = useToggle();
  const [isTextExpanded, expandText] = useToggle();
  const [shouldReload, toggleShouldReload] = useToggle();

  const { plantName, seoOptions } = usePlantSeo(plantDetails);

  const onCloseQrModal = () => {
    toggleQrScanning();

    if (shouldReload) {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (window) {
      const selectorsByUserAgent = getSelectorsByUserAgent(window.navigator.userAgent);
      setIsMobileDevice(selectorsByUserAgent.isMobile);
    }
  }, []);
  const { isEnLocale } = useLocale();

  const name = isEnLocale ? plantDetails.name_en : plantDetails.name;

  const renderContent = () => {
    if (!plantDetails) return <EmptyContent />;

    const {
      natural_habitat,
      natural_habitat_img_optimized,
      natural_habitat_img,
      natural_status,
      planting_year,
      images,
      history,
      useful_properties,
      features,
      x_coord,
      y_coord,
      name_lat,
    } = plantDetails;

    const isTextMoreThanLimit = natural_habitat.length > NATURAL_HABITAT_CHARACTER_LIMIT;
  
    return (
      <>
        <div className="flex flex-col pt-20 md:gap-16 md:flex-row md:pt-44 w-full">
          <div className="w-full md:w-80 md:min-w-80 md:max-w-80 md:pl-4 xl:pl-6">
            <h2 className="mb-5 text-lg font-medium italic text-green md:text-xl">{name_lat}</h2>
            <h1 className="mb-2 text-lg font-bold text-black-900 md:text-[26px]">{plantName}</h1>
            <hr className="my-2" />
            <div className="flex items-center justify-between md:flex-col md:items-start">
              <h3 className="flex text-base-md font-bold md:text-lg">{t('plant.info.year')}:</h3>
              <p className="w-full max-w-[110px] font-medium md:mt-2">{planting_year}</p>
            </div>
            <div className="mt-4 flex flex-col justify-between">
              <h3 className="text-base-md font-bold md:text-lg">{t('plant.info.habitat')}</h3>
              {isTextMoreThanLimit && !isTextExpanded ? (
                <div className="max-w-full">
                  <p className=" font-medium md:mt-2 ">
                    {natural_habitat.substring(0, NATURAL_HABITAT_CHARACTER_LIMIT)}...
                  </p>
                  <Button
                    variant={Colors.transparent}
                    className="flex items-center !p-0 font-bold underline md:ml-auto"
                    onClick={expandText}
                  >
                    <span className="mr-3">{t('plant.info.read-more')}</span>
                    {Icons({ name: IconsTypes.ARROW_DOWN, iconProps: { color: 'currentColor' } })}
                  </Button>
                </div>
              ) : (
                <p className="w-full font-medium md:mt-2 md:max-w-full">{natural_habitat}</p>
              )}
            </div>
            {natural_habitat_img && (
              <div>
                <div className="mt-5 max-w-[205px]">
                  <Image optimized={natural_habitat_img_optimized} alt={name} src={natural_habitat_img} />
                </div>
                <Button variant={Colors.transparent} className="flex items-center !px-0" onClick={toggleExpandMap}>
                  {Icons({ name: IconsTypes.EXPAND })}
                  <span className="ml-3 font-bold text-black-800">{t('buttons.expand-map')}</span>
                </Button>
              </div>
            )}
          </div>

          {!!images.length && (
            <div className="mt-7 w-full md:w-[calc(100%-20rem-4rem)] md:mt-0">
              <ImageSlider aspect="aspect-video" sliderImages={images} name={name} />
            </div>
          )}
        </div>

        <div className="mt-10">
          <h2 className="text-center text-md font-bold md:text-3xl">{t('plant.facts.title')}</h2>
          <hr className="mb-5 mt-2" />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* all these properties are required on admin panel before creating plant
        so they're should be available here without any checking like natural_status && (...) */}
            <div className="w-full rounded-lg bg-white p-6 md:p-11 ">
              <div className="flex items-center">
                <div>{Icons({ name: IconsTypes.NATURAL_STATUS })}</div>
                <h3 className="ml-3 text-md md:text-3xl">{t('plant.facts.statusTitle')}</h3>
              </div>
              <p className="mt-6 font-medium">{natural_status}</p>
            </div>
            <div className="w-full rounded-lg bg-white p-6 md:p-11 ">
              <div className="flex items-center">
                <div>{Icons({ name: IconsTypes.LONG_LIVING })}</div>
                <h3 className="ml-3 text-md md:text-3xl">{t('plant.facts.yearsTitle')}</h3>
              </div>
              <p className="mt-6 font-medium">{features}</p>
            </div>
            <div className="w-full rounded-lg bg-white p-6 md:p-11 ">
              <div className="flex items-center">
                <div>{Icons({ name: IconsTypes.BENEFITS })}</div>
                <h3 className="ml-3 text-md md:text-3xl">{t('plant.facts.propertiesTitle')}</h3>
              </div>
              <p className="mt-6 font-medium">{useful_properties}</p>
            </div>
            <div className="w-full rounded-lg bg-white p-6 md:p-11 ">
              <div className="flex items-center">
                <div>{Icons({ name: IconsTypes.HISTORY_MEANING })}</div>
                <h3 className="ml-3 text-md md:text-3xl">{t('plant.facts.meaningTitle')}</h3>
              </div>
              <p className="mt-6 font-medium">{history}</p>
            </div>
          </div>

          <div className="my-11 flex flex-col items-center justify-center md:flex-row">
            {isMobileDevice && (
              <>
                <Button type="button" className="flex font-bold" onClick={toggleQrScanning}>
                  {Icons({ name: IconsTypes.QRCODE })}
                  <span className="ml-3">{t('buttons.scan-QR')}</span>
                </Button>
                <p className="my-5 md:hidden">{t('text.or')}</p>
              </>
            )}

            <Link href="/page/interactive-map">
              <Button variant={Colors.secondary} type="button" className="font-bold md:ml-7">
                {t('buttons.review-map')}
              </Button>
            </Link>
          </div>
        </div>

        <PlantMapLocation
          coords={{
            x: x_coord,
            y: y_coord,
          }}
          plantName={plantName}
          mainImage={images.find((img) => img.is_main) as IPlantImage}
        />
      </>
    );
  };

  return (
    <Layout siteMenu={siteMenu} seoOptions={seoOptions}>
      <div className="container !pb-0">{renderContent()}</div>

      {expandMap && plantDetails.natural_habitat_img && (
        <Modal className="w-full max-w-[90vw] max-h-[90vh] bg-transparent" onClose={toggleExpandMap}>
          <Image
            className="h-full w-full rounded-lg max-w-[90vw] max-h-[90vh] object-contain"
            optimized={plantDetails.natural_habitat_img_optimized}
            alt={name}
            src={plantDetails.natural_habitat_img}
          />
        </Modal>
      )}

      {isQrScanning && (
        <Modal
          className="flex w-screen flex-col items-center justify-center p-4 md:w-full md:max-w-2xl md:py-8 lg:py-12"
          onClose={onCloseQrModal}
        >
          <h4 className="mb-2 text-center">{t('modals.qr-scanner.hint')}</h4>
          <QrScannerComponent shouldReload={shouldReload} toggleShouldReload={toggleShouldReload} />
        </Modal>
      )}
    </Layout>
  );
};

interface IParams extends ParsedUrlQuery {
  type: string;
  locale: Locale;
}

interface PlantPageStaticProps extends GetStaticProps {
  params: IParams;
}

export async function getStaticPaths() {
  const request = api({});
  const plants = await request<IPlantDetails[]>('plants/');

  const plantsMapped = plants.data.map((plant) => ({
    params: {
      type: plant.slug,
    },
  }));

  const paths = getI18nPaths()
    .filter(({ params: { locale } }) => locale === Locale.UA)
    .map(({ params: paramsLocale }) => {
      return plantsMapped.map(({ params: paramsPaths }) => ({
        params: { ...paramsPaths, ...paramsLocale },
      }));
    })
    .flat();

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }: PlantPageStaticProps) {
  const { type, locale } = params;
  const request = api({ locale: locale });

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<IPlantDetails>(`plants/detail/${type}/`),
  ]);

  const values = responses.map((val) => (val.status === 'fulfilled' ? val.value.data : undefined));
  const [siteMenu, plantDetails] = values;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      plantDetails: plantDetails ?? null,
    },
  };
}

export default PlantTypePage;
